import { useState, useEffect } from 'react';
import Calendar from './Calendar';
import LeaveModal from './LeaveModal';
import ReviewModal from './ReviewModal';
import { api } from '../api';
import { LEAVE_TYPES, STATUS_LABELS, formatDate } from '../constants';

export default function CalendarPage({ user }) {
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [reviewLeave, setReviewLeave] = useState(null);
  const [selectedRange, setSelectedRange] = useState(null);
  const [toast, setToast] = useState(null);

  const fetchLeaves = async () => {
    try {
      const data = await api.getLeaves();
      setLeaves(data);
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLeaves(); }, []);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const handleSelectRange = (from, to) => {
    setSelectedRange({ from, to });
    setShowLeaveModal(true);
  };

  const handleSaved = (leave) => {
    setLeaves(prev => [leave, ...prev]);
    setShowLeaveModal(false);
    setSelectedRange(null);
    showToast('Wniosek złożony — manager zostanie powiadomiony e-mailem.');
  };

  const handleReviewed = (leaveId, status) => {
    setLeaves(prev => prev.map(l => l.id === leaveId ? { ...l, status } : l));
    showToast(status === 'approved' ? 'Wniosek zatwierdzony' : 'Wniosek odrzucony', status === 'approved' ? 'success' : 'error');
  };

  const handleDelete = async (leaveId) => {
    if (!window.confirm('Anulować ten wniosek?')) return;
    try {
      await api.deleteLeave(leaveId);
      setLeaves(prev => prev.filter(l => l.id !== leaveId));
      showToast('Wniosek anulowany');
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const pendingLeaves = leaves.filter(l => l.status === 'pending');
  const myLeaves = leaves.filter(l => l.user_id === user.id);

  return (
    <div>
      {toast && (
        <div style={{ position: 'fixed', top: 70, right: 20, zIndex: 9999, maxWidth: 360 }}>
          <div className={`alert alert-${toast.type === 'error' ? 'error' : 'success'}`} style={{ boxShadow: 'var(--shadow-lg)' }}>
            {toast.msg}
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="stats-grid">
        {user.role === 'manager' && (
          <div className="stat-card" style={{ borderTopColor: pendingLeaves.length > 0 ? '#e8a000' : 'var(--dartom-green)' }}>
            <div className="stat-value" style={{ color: pendingLeaves.length > 0 ? '#c68900' : undefined }}>{pendingLeaves.length}</div>
            <div className="stat-label">Oczekuje na decyzję</div>
          </div>
        )}
        <div className="stat-card">
          <div className="stat-value">{myLeaves.filter(l => l.status === 'approved').length}</div>
          <div className="stat-label">{user.role === 'manager' ? 'Moje zatwierdzone' : 'Zatwierdzone urlopy'}</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{myLeaves.filter(l => l.status === 'pending').length}</div>
          <div className="stat-label">Oczekujące wnioski</div>
        </div>
        {user.role === 'manager' && (
          <div className="stat-card">
            <div className="stat-value">{leaves.filter(l => l.status === 'approved').length}</div>
            <div className="stat-label">Wszystkie zatwierdzone</div>
          </div>
        )}
      </div>

      {/* Pending alerts for manager */}
      {user.role === 'manager' && pendingLeaves.length > 0 && (
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="card-header">
            <span className="card-title">⏳ Wnioski do rozpatrzenia ({pendingLeaves.length})</span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Pracownik</th>
                  <th>Typ</th>
                  <th>Od</th>
                  <th>Do</th>
                  <th>Dni</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {pendingLeaves.map(l => {
                  const typeConf = LEAVE_TYPES[l.type] || LEAVE_TYPES.other;
                  return (
                    <tr key={l.id}>
                      <td style={{ fontWeight: 600 }}>{l.user_name}</td>
                      <td><span className="badge" style={{ background: typeConf.bg, color: typeConf.color }}>{typeConf.label}</span></td>
                      <td>{formatDate(l.date_from)}</td>
                      <td>{formatDate(l.date_to)}</td>
                      <td>{l.days_count}</td>
                      <td>
                        <button className="btn btn-primary btn-sm" onClick={() => setReviewLeave(l)}>Rozpatrz</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Calendar */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <h2 style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 20, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--dartom-green-dark)' }}>
            Kalendarz urlopów
          </h2>
          <button className="btn btn-yellow" onClick={() => { setSelectedRange(null); setShowLeaveModal(true); }}>
            + Nowy wniosek
          </button>
        </div>
        {loading ? (
          <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>Ładowanie...</div>
        ) : (
          <Calendar
            leaves={leaves}
            user={user}
            onSelectRange={user.role !== 'manager' ? handleSelectRange : null}
          />
        )}
      </div>

      {/* My leaves list */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">{user.role === 'manager' ? 'Wszystkie wnioski' : 'Moje wnioski'}</span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                {user.role === 'manager' && <th>Pracownik</th>}
                <th>Typ</th>
                <th>Od</th>
                <th>Do</th>
                <th>Dni</th>
                <th>Status</th>
                <th>Uwagi</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(user.role === 'manager' ? leaves : myLeaves).length === 0 ? (
                <tr><td colSpan={9} style={{ textAlign: 'center', color: 'var(--text3)', padding: 30 }}>Brak wniosków</td></tr>
              ) : (user.role === 'manager' ? leaves : myLeaves).map(l => {
                const typeConf = LEAVE_TYPES[l.type] || LEAVE_TYPES.other;
                const statusConf = STATUS_LABELS[l.status] || STATUS_LABELS.pending;
                return (
                  <tr key={l.id}>
                    {user.role === 'manager' && <td style={{ fontWeight: 600 }}>{l.user_name}</td>}
                    <td><span className="badge" style={{ background: typeConf.bg, color: typeConf.color }}>{typeConf.label}</span></td>
                    <td>{formatDate(l.date_from)}</td>
                    <td>{formatDate(l.date_to)}</td>
                    <td>{l.days_count}</td>
                    <td><span className="badge" style={{ background: statusConf.bg, color: statusConf.color }}>{statusConf.label}</span></td>
                    <td style={{ maxWidth: 200, fontSize: 13, color: 'var(--text2)' }}>
                      {l.note && <div title={l.note} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.note}</div>}
                      {l.manager_note && <div style={{ color: 'var(--text3)' }} title={l.manager_note}>Manager: {l.manager_note}</div>}
                    </td>
                    <td>
                      {user.role === 'manager' && l.status === 'pending' && (
                        <button className="btn btn-primary btn-sm" onClick={() => setReviewLeave(l)}>Rozpatrz</button>
                      )}
                      {l.status === 'pending' && (l.user_id === user.id || user.role === 'manager') && (
                        <button className="btn btn-outline btn-sm" style={{ marginLeft: 4 }} onClick={() => handleDelete(l.id)}>Anuluj</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showLeaveModal && (
        <LeaveModal
          initialFrom={selectedRange?.from}
          initialTo={selectedRange?.to}
          onClose={() => { setShowLeaveModal(false); setSelectedRange(null); }}
          onSaved={handleSaved}
        />
      )}

      {reviewLeave && (
        <ReviewModal
          leave={reviewLeave}
          onClose={() => setReviewLeave(null)}
          onReviewed={handleReviewed}
        />
      )}
    </div>
  );
}
